/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FileType } from 'src/core/types/FileType';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { QnaMakerProjectStore } from 'src/qnaMaker/core/stores/QnaMakerProjectStore';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaMakerProjectsTableCommandBarProps = TableCommandBarProps<QnaMakerProjectStore> & {
    projectsCount: number;
    onDeleteProject: () => boolean | void;
    onCreateProject: () => void;
    onExportProject: (fileType: Extract<FileType, 'tsv' | 'excel'>) => boolean | void;
    onImportProject: (fileType: Extract<FileType, 'tsv' | 'excel'>) => boolean | void;
    isCreateProjectModalHidden: boolean;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.searchPlaceholder',
        defaultMessage: 'Search projects...'
    },
    createProject: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.createProject',
        defaultMessage: 'Create new project'
    },
    deleteProject: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.deleteProject',
        defaultMessage: 'Delete'
    },
    exportProject: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.exportProject',
        defaultMessage: 'Export'
    },
    exportProjectExcel: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.exportProjectExcel',
        defaultMessage: 'Export as Excel'
    },
    exportProjectTsv: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.exportProjectTsv',
        defaultMessage: 'Export as TSV'
    },
    importProject: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.importProject',
        defaultMessage: 'Import'
    },
    importProjectExcel: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.importProjectExcel',
        defaultMessage: 'Import as Excel'
    },
    importProjectTsv: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.importProjectTsv',
        defaultMessage: 'Import as TSV'
    },
    count: {
        id: 'qnaMaker.qnaMakerProjectsTableCommandBar.count',
        defaultMessage: 'items in list'
    }
});

export const QnaMakerProjectsTableCommandBar = observer((props: QnaMakerProjectsTableCommandBarProps) => {
    const { selectedItems = [], query = '', onQueryChange, projectsCount, isCreateProjectModalHidden } = props;
    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();
    const createProjectItemIdRef = React.useRef<string>(getId());

    const { isSignedIn } = rootStore.authStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeleteProject } = mapUserRoleToUserActions(userRole);

    React.useEffect(() => () => dequeueTeachingBubble('QnaCreateProject'), []);

    React.useEffect(() => {
        if (isSignedIn && canWrite && isCreateProjectModalHidden && !uiStore.teachingBubbles.QnaCreateProject) {
            createTeachingBubble(
                'QnaCreateProject',
                localize(qnaMakerTeachingBubbleMessages.createProjectBody),
                localize(qnaMakerTeachingBubbleMessages.createProjectTitle),
                `#${createProjectItemIdRef.current}`,
                true
            );
        } else {
            dequeueTeachingBubble('QnaCreateProject');
        }
    }, [canWrite, isCreateProjectModalHidden, Object.values(uiStore.teachingBubbles)]);

    const exportSubMenuItems: IContextualMenuProps = {
        items: [
            { key: 'exportExcel', name: localize(messages.exportProjectExcel), onClick: () => props.onExportProject('excel') },
            { key: 'exportTsv', name: localize(messages.exportProjectTsv), onClick: () => props.onExportProject('tsv') }
        ]
    };

    const importSubMenuItems: IContextualMenuProps = {
        items: [
            { key: 'importExcel', name: localize(messages.importProjectExcel), onClick: () => props.onImportProject('excel') },
            { key: 'importTsv', name: localize(messages.importProjectTsv), onClick: () => props.onImportProject('tsv') }
        ]
    };

    const commands: ICommandBarItemProps[] = [
        {
            key: 'create',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="creatProjectButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onCreateProject}
                            iconProps={{ iconName: 'Add' }}
                            data-automation-id="createButton"
                            id={createProjectItemIdRef.current}
                            text={localize(messages.createProject)}
                            name={localize(messages.createProject)}
                            disabled={!isSignedIn || !canWrite || item.disabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'export',
            subMenuProps: exportSubMenuItems,
            iconProps: { iconName: 'Download' },
            name: localize(messages.exportProject)
        },
        {
            key: 'import',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="importProjectButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            menuProps={importSubMenuItems}
                            iconProps={{ iconName: 'Upload' }}
                            data-automation-id="importProject"
                            text={localize(messages.importProject)}
                            name={localize(messages.importProject)}
                            disabled={!isSignedIn || !canWrite || item.disabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canDeleteProject || item.disabled}
                    data-automation-id="deleteProjectButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onDeleteProject}
                            data-automation-id="deleteProject"
                            iconProps={{ iconName: 'Delete' }}
                            text={localize(messages.deleteProject)}
                            name={localize(messages.deleteProject)}
                            disabled={!canDeleteProject || item.disabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['create', 'import'],
        ['rename', 'import', 'export', 'delete'],
        ['delete'],
        selectedItems.length
    );

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'projectsCount',
            onRender: () => (
                <LuisText styles={{ root: { display: 'flex', alignItems: 'center', marginRight: '5px' } }}>
                    {projectsCount} {localize(messages.count)}
                </LuisText>
            )
        },
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedSearchBox
                    size={36}
                    value={query}
                    styles={{ root: { margin: 'auto' } }}
                    ariaLabel={localize(messages.searchPlaceholder)}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        }
    ];

    return <LuisCommandBar items={items} farItems={farItems} />;
});
